import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { COMPANY_INFO, buildWhatsAppUrl } from '../config/company';
import { trackContactClick, trackGenerateLead } from '../lib/analytics';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';
import { CalendarModal } from '../components/CalendarModal';
import { useSearchBar } from '../contexts/SearchBarContext';
import { ProIcon } from '../components/ProIcon';

export const Contact = () => {
  const { searchParams, updateSearchParams } = useSearchBar();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+44',
    customCountryCode: '',
    userType: 'guest',
    message: '',
    website: '' // honeypot field - should be empty
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Date selection for guest inquiries - local state (NOT synced with global search)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [isFlexible, setIsFlexible] = useState(false);
  const [flexibleDays, setFlexibleDays] = useState(2);
  const [showCalendar, setShowCalendar] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check - if website field is filled, it's likely a bot
    if (formData.website) {
      console.log('Bot detected - form rejected');
      return;
    }
    
    // Validation checks
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      alert('Please enter your full name (at least 2 characters)');
      return;
    }
    
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }
    
    // Phone validation - check if we have either a phone number or custom country code
    const phoneToValidate = formData.countryCode === 'other' 
      ? formData.customCountryCode 
      : formData.phone;
    
    if (!phoneToValidate.trim()) {
      alert('Please enter your phone number');
      return;
    }
    
    if (formData.countryCode === 'other' && !/^\+[0-9]{1,4}$/.test(formData.customCountryCode)) {
      alert('Please enter a valid country code starting with + (e.g., +34)');
      return;
    }
    
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      alert('Please tell us about your trip (at least 10 characters)');
      return;
    }
    
    setIsSubmitting(true);
    
    trackGenerateLead('contact_form');
    console.log('Form submitted:', formData);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Special validation for custom country code
    if (name === 'customCountryCode') {
      // Ensure it starts with + and only contains digits after, max 4 chars (+123)
      let sanitized = value.replace(/[^0-9+]/g, '');
      if (sanitized && !sanitized.startsWith('+')) {
        sanitized = '+' + sanitized;
      }
      // Limit to + and 3 digits max
      if (sanitized.length > 4) {
        sanitized = sanitized.slice(0, 4);
      }
      setFormData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const contactInfo = [
    {
      icon: <ProIcon icon="circle-location-arrow" family="solid" className="text-xl" />,
      title: 'Office',
      content: [COMPANY_INFO.address.line1, COMPANY_INFO.address.line2]
    },
    {
      icon: (
        <div className="relative inline-flex items-center justify-center">
          <ProIcon icon="phone-connection" family="solid" className="text-xl" />
        </div>
      ),
      title: 'Phone / WhatsApp',
      content: [COMPANY_INFO.phone],
      href: COMPANY_INFO.phoneHref,
      onClick: () => trackContactClick('phone', 'contact_page')
    },
    {
      icon: <ProIcon icon="envelope-open" family="solid" className="text-xl" />,
      title: 'Email',
      content: [],
      useObfuscatedEmail: true,
      href: COMPANY_INFO.emailHref,
      onClick: () => trackContactClick('email', 'contact_page')
    },
    {
      icon: <ProIcon icon="clock" family="solid" className="text-xl" />,
      title: 'Availability',
      content: ['Monday - Sunday: 9:00 AM - 8:00 PM', 'Emergency guest line: 24/7']
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="ui-eyebrow-brand mb-4">Contact SUNMARBELLA</p>
          <h1 className="ui-page-title mb-6 max-w-4xl mx-auto">Tell Us What You Need.</h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Guest or owner, send the basics. We will come back with clear next steps.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 sm:p-8 md:p-10 border border-slate-100"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="ui-input"
                  required
                  minLength={2}
                  maxLength={50}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="ui-input"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                  Phone Number
                </label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="ui-input w-full sm:w-auto px-3"
                  >
                    <option value="+44">🇬🇧 UK (+44)</option>
                    <option value="+34">🇪🇸 ES (+34)</option>
                    <option value="+47">🇳🇴 NO (+47)</option>
                    <option value="+46">🇸🇪 SE (+46)</option>
                    <option value="+31">🇳🇱 NL (+31)</option>
                    <option value="+49">🇩🇪 DE (+49)</option>
                    <option value="+1">🇺🇸 US (+1)</option>
                    <option value="+33">🇫🇷 FR (+33)</option>
                    <option value="+39">🇮🇹 IT (+39)</option>
                    <option value="+351">🇵🇹 PT (+351)</option>
                    <option value="other">Other...</option>
                  </select>
                  {formData.countryCode === 'other' ? (
                    <input
                      type="text"
                      id="customCountryCode"
                      name="customCountryCode"
                      value={formData.customCountryCode}
                      onChange={handleChange}
                      placeholder="+34851000158"
                      className="ui-input flex-1"
                    />
                  ) : (
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="851 000 158"
                      className="ui-input flex-1"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  I'm contacting you as
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    formData.userType === 'guest' 
                      ? 'border-brand-ocean bg-brand-ocean/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="userType"
                      value="guest"
                      checked={formData.userType === 'guest'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.userType === 'guest' ? 'border-brand-ocean' : 'border-slate-400'
                    }`}>
                      {formData.userType === 'guest' && <div className="w-2.5 h-2.5 rounded-full bg-brand-ocean" />}
                    </div>
                    <span className={`text-sm font-medium ${formData.userType === 'guest' ? 'text-brand-ocean' : 'text-slate-600'}`}>
                      Guest looking for a rental
                    </span>
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    formData.userType === 'owner' 
                      ? 'border-brand-ocean bg-brand-ocean/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="userType"
                      value="owner"
                      checked={formData.userType === 'owner'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.userType === 'owner' ? 'border-brand-ocean' : 'border-slate-400'
                    }`}>
                      {formData.userType === 'owner' && <div className="w-2.5 h-2.5 rounded-full bg-brand-ocean" />}
                    </div>
                    <span className={`text-sm font-medium ${formData.userType === 'owner' ? 'text-brand-ocean' : 'text-slate-600'}`}>
                      Property owner
                    </span>
                  </label>
                </div>
              </div>

              {/* Date selection - only for guests */}
              {formData.userType === 'guest' && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    When are you planning to visit?
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCalendar(true)}
                    className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-slate-200 hover:border-brand-ocean hover:bg-brand-ocean/5 transition-all duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-ocean/10 flex items-center justify-center text-brand-ocean">
                    <ProIcon icon="calendar-range" family="light" className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        {startDate && endDate ? (
                          <>
                            <div className="text-sm font-bold text-slate-900">
                              {startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — {endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </div>
                            <div className="text-xs text-slate-500">
                              {Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))} nights
                              {isFlexible && <span className="ml-1 text-brand-ocean font-medium">(±{flexibleDays} flexible)</span>}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm font-medium text-slate-600">Select your dates</div>
                        )}
                      </div>
                    </div>
                    <ProIcon icon="chevron-right" family="light" className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              )}

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                  How Can We Help?
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder={formData.userType === 'guest' 
                    ? "Tell us your group size, preferred areas, important property features (pool, beachfront, etc.), and any specific area features you're looking for."
                    : "Tell us about your property area, property details, and when you would be available to meet with us."
                  }
                  className="ui-input resize-none"
                  required
                  minLength={10}
                  maxLength={1000}
                />
              </div>

              {/* Honeypot field - hidden from users, visible to bots */}
              <div className="hidden" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-sun hover:bg-brand-sun/90 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-brand-sun/30 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <ProIcon icon="spinner" family="solid" className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <ProIcon icon="paper-plane" family="solid" className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>

            {/* Calendar Modal for Guest Date Selection */}
            <CalendarModal
              isOpen={showCalendar}
              startDate={startDate}
              endDate={endDate}
              onClose={() => setShowCalendar(false)}
              onDatesChange={(dates, flexible, days) => {
                setDateRange(dates);
                setIsFlexible(flexible || false);
                setFlexibleDays(days || 2);
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">What Happens Next</h2>
              <p className="text-slate-600">
                We reply quickly, shortlist the right homes, answer practical questions, and help owners understand whether their property fits our collection.
              </p>
            </div>

            <div className="space-y-4">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-2xl bg-white border border-slate-100 hover:shadow-lg hover:shadow-slate-200/50 transition-all duration-200"
                >
                  <div className="w-12 h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center flex-shrink-0 text-brand-ocean">
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    {item.href ? (
                      item.useObfuscatedEmail ? (
                        <ObfuscatedEmail className="text-brand-ocean hover:text-brand-ocean/80 transition-colors" />
                      ) : (
                        <a
                          href={item.href}
                          onClick={item.onClick}
                          className="text-brand-ocean hover:text-brand-ocean/80 transition-colors"
                        >
                          {item.content[0]}
                        </a>
                      )
                    ) : (
                      <div className="text-slate-600 text-sm space-y-0.5">
                        {item.content.map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.9 }}
              className="p-6 rounded-2xl bg-gradient-to-br from-brand-ocean/5 to-brand-sun/5 border border-brand-ocean/10"
            >
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-brand-ocean flex items-center justify-center text-white text-xs font-bold">DM</div>
                  <div className="w-8 h-8 rounded-full bg-brand-sun flex items-center justify-center text-white text-xs font-bold">JS</div>
                  <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white text-xs font-bold">+5</div>
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-slate-900">Local team ready to help</p>
                  <p className="text-slate-500">Response time: under 2 hours</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
